//frontend/src/components/marketing/Hero.tsx

import React, { Suspense, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, TrendingUp, Zap, Rocket, Building2, Loader } from 'lucide-react';
import { getPublicDemos, submitQuickDemo, type PublicDemo, type QuickDemoResponse } from '../../services/demoApi';
import { validateDemoIdea } from '../../utils/validateDemoIdea';
import { API_CONFIG } from '../../utils/config';
import { buildMarketRequestHeaders } from '../../utils/marketContext';
import {
  buildPreviewAuthPath,
  buildPreviewResumePath,
  savePreviewIntent,
} from '../../utils/previewIntent';
import logger from '../../utils/logger';
import LocalizationFallbackBanner from '../shared/LocalizationFallbackBanner';
import type { TrendingIdea } from './HeroPreviewCard';
import ProgramHeroPreview from './ProgramHeroPreview';
import '../../styles/marketing.css';

const HeroPreviewCard = React.lazy(() => import('./HeroPreviewCard'));

interface HeroProps {
  audienceMode: 'founder' | 'program';
  onModeChange: (mode: 'founder' | 'program') => void;
}

interface DemoRequestError extends Error {
  status?: number;
}

const Hero: React.FC<HeroProps> = ({ audienceMode, onModeChange }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  const localizedT = useMemo(() => i18n.getFixedT(activeLanguage), [i18n, activeLanguage]);

  const [activeDemo, setActiveDemo] = useState<number>(0);
  const [animateScore, setAnimateScore] = useState(0);
  const [showDemoCard, setShowDemoCard] = useState(false); // New: controls if demo card is visible
  
  // Real demo data from API
  const [publicDemoFeed, setPublicDemoFeed] = useState<{
    language: string;
    demos: PublicDemo[];
  }>({ language: activeLanguage, demos: [] });
  const [userDemo, setUserDemo] = useState<QuickDemoResponse | null>(null);
  const [demoInput, setDemoInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [error, setError] = useState('');
  const [limitReached, setLimitReached] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // ✅ AI-generated trending ideas for landing page
  const [trendingIdeas, setTrendingIdeas] = useState<TrendingIdea[]>([]);
  const [activeTrendingIndex, setActiveTrendingIndex] = useState<number>(0);
  // DEC-2026-09 Phase 2 Step E: localization fallback meta from /trending response.
  // Set when backend signals { localizationFallback: true, lang, effectiveLang }.
  const [serendipityFallback, setSerendipityFallback] = useState<{
    lang: string;
    effectiveLang: string;
  } | null>(null);
  const [viewingUserDemo, setViewingUserDemo] = useState<boolean>(true); // Track which view is active
  // Give a useful summary first; keep deeper signal context one tap away.
  const [showMoreDetails, setShowMoreDetails] = useState<boolean>(false);
  
  // Helper to get text based on audience mode
  const getAudienceText = (founderKey: string, programKey: string) => {
    return audienceMode === 'founder' ? t(founderKey) : t(programKey);
  };
  
  // ✅ Rotating placeholder examples for better engagement
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const placeholderExamples = useMemo(() => [
    localizedT('placeholderExample1'),
    localizedT('placeholderExample2'),
    localizedT('placeholderExample3'),
    localizedT('placeholderExample4'),
    localizedT('placeholderExample5')
  ], [localizedT]);

  // Fallback static demos (if API fails) - memoized to prevent re-renders
  const fallbackDemos: PublicDemo[] = useMemo(() => [
    {
      demoId: 0,
      idea: localizedT('fallbackDemoIdea1'),
      overallScore: 78,
      scoreLabel: localizedT('fallbackDemoScoreLabel1'),
      marketSize: '',
      quickInsights: [
        localizedT('fallbackDemoInsight1_1'),
        localizedT('fallbackDemoInsight1_2'),
        localizedT('fallbackDemoInsight1_3')
      ],
      keyOpportunity: localizedT('fallbackDemoOpportunity1'),
      viewCount: 142,
      upvoteCount: 28,
      createdAt: new Date().toISOString(),
      evidenceSummary: {
        totalSources: 0,
        platforms: [],
        sampleSources: []
      },
      isSample: true
    },
    {
      demoId: 1,
      idea: localizedT('fallbackDemoIdea2'),
      overallScore: 72,
      scoreLabel: localizedT('fallbackDemoScoreLabel2'),
      marketSize: '',
      quickInsights: [
        localizedT('fallbackDemoInsight2_1'),
        localizedT('fallbackDemoInsight2_2'),
        localizedT('fallbackDemoInsight2_3')
      ],
      keyOpportunity: localizedT('fallbackDemoOpportunity2'),
      viewCount: 98,
      upvoteCount: 15,
      createdAt: new Date().toISOString(),
      evidenceSummary: {
        totalSources: 0,
        platforms: [],
        sampleSources: []
      },
      isSample: true
    },
    {
      demoId: 2,
      idea: localizedT('fallbackDemoIdea3'),
      overallScore: 85,
      scoreLabel: localizedT('fallbackDemoScoreLabel1'),
      marketSize: '',
      quickInsights: [
        localizedT('fallbackDemoInsight3_1'),
        localizedT('fallbackDemoInsight3_2'),
        localizedT('fallbackDemoInsight3_3')
      ],
      keyOpportunity: localizedT('fallbackDemoOpportunity3'),
      viewCount: 203,
      upvoteCount: 42,
      createdAt: new Date().toISOString(),
      evidenceSummary: {
        totalSources: 0,
        platforms: [],
        sampleSources: []
      },
      isSample: true
    }
  ], [localizedT]); // Re-create when language changes

  // ✅ NORMALIZE: Ensure ALL demos have same structure with fallbacks
  const normalizeDemoData = (demo: PublicDemo | QuickDemoResponse): PublicDemo => {
    // Ensure evidenceSummary exists
    const evidenceSummary = demo.evidenceSummary && demo.evidenceSummary.totalSources > 0 
      ? demo.evidenceSummary 
      : {
          totalSources: 0,
          platforms: [],
          sampleSources: []
        };

    // Ensure quickInsights has at least 2 items
    const quickInsights = demo.quickInsights && demo.quickInsights.length >= 2
      ? demo.quickInsights
      : [
          demo.quickInsights?.[0] || t('normalizedFallbackInsight1'),
          t('normalizedFallbackInsight2'),
          t('normalizedFallbackInsight3')
        ];

    // Ensure PublicDemo-specific fields exist (for QuickDemoResponse)
    const viewCount = 'viewCount' in demo ? demo.viewCount : 0;
    const upvoteCount = 'upvoteCount' in demo ? demo.upvoteCount : 0;
    const createdAt = 'createdAt' in demo ? demo.createdAt : new Date().toISOString();

    return {
      demoId: demo.demoId,
      idea: demo.idea,
      overallScore: demo.overallScore,
      scoreLabel: demo.scoreLabel,
      marketSize: demo.marketSize,
      countryCode: demo.countryCode || null,
      currencyCode: demo.currencyCode || null,
      marketName: demo.marketName || null,
      marketFallback: Boolean(demo.marketFallback),
      // NEW: 4 distinct fields for unified card display
      painPoint: demo.painPoint,
      keyInsight: demo.keyInsight,
      whyNow: demo.whyNow,
      keyOpportunity: demo.keyOpportunity || t('normalizedFallbackOpportunity'),
      // AI-generated business metrics — MUST be preserved, not stripped
      businessMetrics: demo.businessMetrics || null,
      // Legacy: for backward compatibility
      quickInsights,
      viewCount,
      upvoteCount,
      createdAt,
      evidenceSummary,
      valueProposition: demo.valueProposition,
      isSample: demo.isSample
    };
  };

  // Never render a completed request from the previous live language while
  // the newly selected language is still loading.
  const publicDemos = publicDemoFeed.language === activeLanguage
    ? publicDemoFeed.demos
    : [];

  // Get current demo (user demo or public demo) and normalize
  const allDemos = userDemo ? [userDemo, ...publicDemos] : (publicDemos.length > 0 ? publicDemos : fallbackDemos);
  const rawDemo = allDemos[activeDemo] || allDemos[0] || fallbackDemos[0];
  const currentDemo = normalizeDemoData(rawDemo);
  const previewsRemaining = typeof userDemo?.demosRemaining === 'number' && Number.isFinite(userDemo.demosRemaining)
    ? Math.max(0, Math.floor(userDemo.demosRemaining))
    : null;
  
  // Animate score on mount and when demo changes
  // MOB-04: Skip animation if user prefers reduced motion
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setAnimateScore(currentDemo.overallScore);
      return;
    }
    setAnimateScore(0);
    let count = 0;
    const target = currentDemo.overallScore;
    const interval = setInterval(() => {
      count += 2;
      if (count >= target) {
        setAnimateScore(target);
        clearInterval(interval);
      } else {
        setAnimateScore(count);
      }
    }, 20);
    return () => clearInterval(interval);
  }, [activeDemo, currentDemo.overallScore]);

  // Fetch public demos on mount (with language filter)
  useEffect(() => {
    let cancelled = false;

    async function loadPublicDemos() {
      try {
        // Get user's current language from i18n (e.g., 'en', 'fr', 'es')
        const demos = await getPublicDemos(3, activeLanguage);
        if (cancelled) return;

        if (demos && demos.length > 0) {
          // ✅ Normalization happens in normalizeDemoData function
          setPublicDemoFeed({ language: activeLanguage, demos });
        } else {
          // Use fallback demos if API returns empty (no demos in user's language)
          setPublicDemoFeed({ language: activeLanguage, demos: fallbackDemos });
        }
      } catch (err) {
        if (cancelled) return;
        logger.warn('Public demos unavailable; using landing fallback demos.');
        // Use fallback demos on error
        setPublicDemoFeed({ language: activeLanguage, demos: fallbackDemos });
      }
    }
    
    loadPublicDemos();
    return () => {
      cancelled = true;
    };
  }, [fallbackDemos, activeLanguage]); // Reload when language changes
  
  // Advance analysis step every ~18s so user knows the system is working
  useEffect(() => {
    if (!isAnalyzing) {
      setAnalysisStep(0);
      return;
    }
    const STEP_DURATIONS = [14000, 16000, 14000, 18000]; // ms per step
    let step = 0;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const advance = (i: number) => {
      const timer = setTimeout(() => {
        step = i + 1;
        setAnalysisStep(step);
        if (step < STEP_DURATIONS.length) advance(step);
      }, STEP_DURATIONS[i]);
      timers.push(timer);
    };
    advance(0);
    return () => timers.forEach(clearTimeout);
  }, [isAnalyzing]);
  
  // ✅ Fetch AI-generated trending ideas for landing page
  // DEC-2026-09 Phase 2 Step E: send x-user-lang header + consume localizationFallback envelope
  useEffect(() => {
    const controller = new AbortController();
    // Never leave suggestions from the previous locale visible while refreshing.
    setTrendingIdeas([]);
    setSerendipityFallback(null);
    setActiveTrendingIndex(0);

    async function loadTrendingIdeas() {
      try {
        const headers = await buildMarketRequestHeaders(activeLanguage);
        if (controller.signal.aborted) return;
        const response = await fetch(`${API_CONFIG.BACKEND_URL}/api/serendipity/trending`, {
          headers,
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (data.ideas && data.ideas.length > 0) {
            setTrendingIdeas(data.ideas);
          }
          // Capture lang/effectiveLang for the fallback banner. Backend returns
          // localizationFallback=true when user's lang has no rows yet and EN
          // rows were served instead. Banner self-guards if lang===effectiveLang.
          if (data.localizationFallback === true && data.lang && data.effectiveLang) {
            setSerendipityFallback({ lang: data.lang, effectiveLang: data.effectiveLang });
          } else {
            setSerendipityFallback(null);
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        logger.error('Failed to load trending ideas:', err);
      }
    }
    loadTrendingIdeas();
    return () => controller.abort();
  }, [activeLanguage]);
  
  // ✅ Rotate placeholder examples every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholderExamples.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [placeholderExamples.length]);
  
  // Handle demo submission
  const handleDemoSubmit = async () => {
    // ✅ CLIENT-SIDE VALIDATION FIRST (better UX)
    const validation = validateDemoIdea(demoInput);
    if (!validation.valid) {
      setError(validation.error || t('validationEnterYourIdea'));
      return;
    }
    
    setIsAnalyzing(true);
    setError('');
    setLimitReached(false);
    
    try {
      const result = await submitQuickDemo({ 
        idea: demoInput.trim(),
        sourcePage: 'homepage'
      }, activeLanguage);
      
      setUserDemo(result);
      setActiveDemo(0); // Switch to user's demo
      setShowDemoCard(true); // Show the demo card
      setDemoInput('');
    } catch (err: unknown) {
      const requestError = err instanceof Error ? err as DemoRequestError : null;
      if (requestError?.status === 429) {
        setLimitReached(true);
        setError(t('demoLimitReached'));
      } else if (requestError?.status === 400) {
        setError(t('enterLegitimateIdea'));
      } else {
        // Generic error
        setError(t('unableToAnalyze'));
      }
      logger.error('Demo submission error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const continueAfterPreviewLimit = () => {
    const idea = demoInput.trim();
    if (!idea) return;

    savePreviewIntent({
      tool: 'concept',
      idea,
      returnPath: buildPreviewResumePath('concept', idea),
    });
    navigate(buildPreviewAuthPath('signup', 'concept', idea));
  };

  return (
    <section className="hero-luxury">
      {/* Animated gradient background */}
      <div className="hero-bg-gradient" />
      <div className="hero-grid-pattern" />
      
      <div className="hero-container">
        {/* Badge + Toggle stacked vertically */}
        <div className="hero-header-stack">
          {/* Badge/Tag */}
          <div className="hero-badge hero-badge-inner">
            <Sparkles size={16} color="var(--primary-500)" />
            <span className="hero-badge-text">
              {audienceMode === 'founder' ? t('heroTaglineFounder') : t('heroTaglineProgram')}
            </span>
          </div>

          {/* AUDIENCE TOGGLE: Founder / Program */}
          <div
            className="hero-audience-toggle"
            role="group"
            aria-label={t('landingAudienceSelectorLabel')}
          >
          <button
            type="button"
            onClick={() => onModeChange('founder')}
            className={`hero-toggle-btn ${audienceMode === 'founder' ? 'hero-toggle-btn--active' : ''}`}
            aria-pressed={audienceMode === 'founder'}
          >
            <Rocket size={14} />
            {t('founderToggle')}
          </button>
          <button
            type="button"
            onClick={() => onModeChange('program')}
            className={`hero-toggle-btn ${audienceMode === 'program' ? 'hero-toggle-btn--active' : ''}`}
            aria-pressed={audienceMode === 'program'}
          >
            <Building2 size={14} />
            {t('programToggle')}
          </button>
          </div>
        </div>

        {/* Hero Title - Dynamic based on audience mode */}
        <h1 className="hero-title-luxury">
          {getAudienceText('heroTitleFounder', 'heroTitleProgram').split('. ').map((part, index, arr) => (
            <React.Fragment key={index}>
              {part}{index < arr.length - 1 ? '.' : ''}
              {index < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h1>

        {/* Subtitle - Dynamic based on audience mode */}
        <h2 className="hero-subtitle">
          {getAudienceText('heroSubtitleFounder', 'heroSubtitleProgram')}
        </h2>

        {/* Description - Dynamic based on audience mode */}
        <p className="hero-description">
          {getAudienceText('heroDescriptionFounder', 'heroDescriptionProgram')}
        </p>

        {audienceMode === 'program' && (
          <ProgramHeroPreview onOpenPreview={() => navigate('/programs/workspace?preview=1')} />
        )}

        {/* ✅ QUICK GENERATE - THE MAIN ACTION */}
        {audienceMode === 'founder' && (
        <div className="hero-demo-box">
          {/* Demo badge */}
          <div className="hero-demo-badge">
            ⚡ {t('heroDemoBadge')}
          </div>
          <h4 className="hero-demo-title">
            <Sparkles size={18} color="var(--primary-500)" />
            {t('validateYourIdeaIn60Seconds')}
          </h4>
          <div className="hero-demo-input-wrap">
                <input
                  ref={inputRef}
                  type="text"
                  value={demoInput}
                  onChange={(e) => setDemoInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || isAnalyzing || !demoInput.trim()) return;
                    if (limitReached) {
                      continueAfterPreviewLimit();
                    } else {
                      handleDemoSubmit();
                    }
                  }}
                  placeholder={placeholderExamples[placeholderIndex]}
                  disabled={isAnalyzing}
                  className={`hero-demo-input ${error ? 'hero-demo-input--error' : ''}`}
                />
              </div>
              
              {error && (
                <p className="hero-demo-error">
                  ⚠️ {error}
                </p>
              )}
              
              <button
                onClick={limitReached ? continueAfterPreviewLimit : handleDemoSubmit}
                disabled={isAnalyzing || !demoInput.trim()}
                className="hero-demo-submit"
              >
                {isAnalyzing ? (
                  <>
                    <Loader size={18} className="spin" />
                    {t('analyzingYourIdeaProgress')}
                  </>
                ) : (
                  <>
                    <Zap size={18} />
                    {t(limitReached ? 'demoLimitContinue' : 'analyzeMyIdeaFree')}
                  </>
                )}
              </button>

              {/* ── Analysis Progress Panel ─────────────────────────────── */}
              {isAnalyzing && (() => {
                const steps = [
                  { label: t('analysisStepGatheringEvidence', 'Checking relevant market signals'), icon: '🔍' },
                  { label: t('analysisStepPricingResearch', 'Checking pricing context'), icon: '💰' },
                  { label: t('analysisStepCategoryAI', 'Identifying the business context'), icon: '🏷️' },
                  { label: t('analysisStepFullAnalysis', 'Building your preview'), icon: '🤖' },
                  { label: t('analysisStepFinalizing', 'Finalizing your preview'), icon: '✨' },
                ];
                return (
                  <div className="hero-analysis-panel">
                    <p className="hero-analysis-header">
                      {t('analysisInProgress', 'Preparing your preview...')}
                    </p>
                    {steps.map((step, i) => {
                      const done = i < analysisStep;
                      const active = i === analysisStep;
                      return (
                        <div key={i} className="hero-analysis-step" style={{ opacity: done ? 0.5 : active ? 1 : 0.3 }}>
                          <span className="hero-analysis-step-icon">
                            {done ? '✓' : step.icon}
                          </span>
                          <span className="hero-analysis-step-label" style={{
                            color: done ? 'var(--text-tertiary)' : active ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            fontWeight: active ? 600 : 400
                          }}>
                            {step.label}
                            {active && (
                              <span className="hero-analysis-dots">
                                {[0,1,2].map(d => (
                                  <span key={d} className="hero-analysis-dot" style={{
                                    animation: `pulse 1.2s ease-in-out ${d * 0.2}s infinite`
                                  }} />
                                ))}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              {/* ───────────────────────────────────────────────────────── */}

              <p className="hero-demo-footer">
                ⚡ {t('analysisTimeNoSignup')}
                {previewsRemaining !== null && (
                  <> • {t('landingPreviewsRemainingToday', { count: previewsRemaining })}</>
                )}
              </p>
              
              {/* Idea Discovery CTA — SmartOnboarding for users without an idea */}
              <div className="hero-idea-discovery">
                <button
                  onClick={() => navigate('/get-started')}
                  className="hero-idea-discovery-btn"
                >
                  {t('autogen.651db8dc')}
                </button>
              </div>
        </div>

        )}

        {/* Trending Ideas Pills - AI-Generated Daily Ideas */}
        {audienceMode === 'founder' && (trendingIdeas.length > 0 || allDemos.length > 0) && (
          <div className="hero-trending-section">
            {/* DEC-2026-09 Phase 2 Step E: localization fallback notice for non-EN users */}
            {serendipityFallback && (
              <LocalizationFallbackBanner
                lang={serendipityFallback.lang}
                effectiveLang={serendipityFallback.effectiveLang}
              />
            )}
            <div className="hero-trending-header">
              <TrendingUp size={14} color="var(--primary-500)" />
              <span className="hero-trending-label">
                {trendingIdeas.length > 0
                  ? t('trendingIdeasToday')
                  : t('sampleIdeaShortcuts')}
              </span>
            </div>
            
            <div className="hero-trending-pills">
              {/* Show user's submitted idea first if it exists - same purple style as AI buttons */}
              {userDemo && (
                <button
                  onClick={() => {
                    setViewingUserDemo(true); // Switch to user demo view
                    setShowDemoCard(true);
                  }}
                  className={`hero-trending-pill hero-trending-pill--user ${viewingUserDemo ? 'hero-trending-pill--active' : ''}`}
                >
                  <span className="hero-trending-pill-content">
                    <Zap size={12} />
                    {userDemo.idea.substring(0, 25)}{userDemo.idea.length > 25 ? '...' : ''}
                  </span>
                </button>
              )}
              
              {/* Show AI-generated trending ideas - purple style with TrendingUp icon */}
              {/* Show 2 AI ideas if user has submitted one, otherwise show 3 */}
              {trendingIdeas.slice(0, userDemo ? 2 : 3).map((idea, index) => {
                const isActiveAI = !viewingUserDemo && activeTrendingIndex === index;
                return (
                <button
                  key={idea.id}
                  onClick={() => {
                    setViewingUserDemo(false); // Switch to AI trending view
                    setActiveTrendingIndex(index);
                    setShowDemoCard(true);
                  }}
                  className={`hero-trending-pill ${isActiveAI ? 'hero-trending-pill--active' : ''}`}
                >
                  <span className="hero-trending-pill-content">
                    <TrendingUp size={12} />
                    {idea.title || idea.idea.substring(0, 25)}
                  </span>
                </button>
              );
              })}

              {/* Keep useful idea shortcuts when the live Serendipity feed is unavailable. */}
              {trendingIdeas.length === 0
                && allDemos
                  .slice(userDemo ? 1 : 0, 3)
                  .map((demo, index) => {
                    const demoIndex = index + (userDemo ? 1 : 0);
                    const isActiveDemo = viewingUserDemo && activeDemo === demoIndex;
                    return (
                      <button
                        key={`sample-${demo.demoId}-${demoIndex}`}
                        onClick={() => {
                          setViewingUserDemo(true);
                          setActiveDemo(demoIndex);
                          setShowDemoCard(true);
                        }}
                        className={`hero-trending-pill ${isActiveDemo ? 'hero-trending-pill--active' : ''}`}
                      >
                        <span className="hero-trending-pill-content">
                          <Sparkles size={12} />
                          {demo.idea.substring(0, 25)}{demo.idea.length > 25 ? '...' : ''}
                        </span>
                      </button>
                    );
                  })}
            </div>
          </div>
        )}

        {/* Result details are loaded only after the founder asks to see them. */}
        {audienceMode === 'founder' && showDemoCard && (trendingIdeas.length > 0 || allDemos.length > 0 || userDemo) && (
          <Suspense
            fallback={(
              <div className="hero-demo-card hero-preview-loading" role="status">
                <Loader size={18} className="spin" />
                <span>{t('heroPreviewLoading')}</span>
              </div>
            )}
          >
            <HeroPreviewCard
              activeTrending={trendingIdeas[activeTrendingIndex]}
              animateScore={animateScore}
              currentDemo={currentDemo}
              hasUserDemo={Boolean(userDemo)}
              showMoreDetails={showMoreDetails}
              viewingUserDemo={viewingUserDemo}
              onBrowseMore={() => {
                inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => inputRef.current?.focus(), 500);
              }}
              onClose={() => setShowDemoCard(false)}
              onContinueIdea={(idea) => navigate('/concept-validator', {
                state: { idea, fromDemo: true },
              })}
              onToggleDetails={() => setShowMoreDetails((current) => !current)}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
};
export default Hero;
